import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';

/**
 * Configuration de TanStack Query.
 *
 * Deux réglages méritent une explication :
 *
 * 1. `retry` ne réessaie JAMAIS une erreur 4xx. Un 403 ou un 404 ne devient pas vrai en
 *    insistant ; réessayer trois fois ne fait que tripler la charge et retarder l'affichage
 *    du message d'erreur. Seules les erreurs réseau et 5xx sont réessayées.
 *
 * 2. `staleTime` non nul par défaut. Avec 0, TanStack Query refait une requête à chaque
 *    montage de composant : passer d'un onglet à l'autre relancerait toutes les requêtes.
 *    30 secondes est un compromis raisonnable pour des données de gestion ; les données
 *    temps réel (état des écrans) surchargent cette valeur au cas par cas.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,

      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }

        return failureCount < 2;
      },
    },

    mutations: {
      // Les mutations ne sont jamais réessayées automatiquement : elles ne sont pas
      // idempotentes. Réessayer « créer une diffusion » créerait deux diffusions.
      retry: false,
    },
  },
});

/**
 * Clés de cache centralisées.
 *
 * Les déclarer en un seul endroit évite les fautes de frappe silencieuses (`['screens']`
 * contre `['screen']`), qui se manifestent par une invalidation qui « ne marche pas » sans
 * aucune erreur visible.
 */
export const queryKeys = {
  currentUser: ['auth', 'me'] as const,
  externalAuthProviders: ['auth', 'external', 'providers'] as const,

  establishments: (filters?: Record<string, unknown>) =>
    filters ? (['establishments', filters] as const) : (['establishments'] as const),
  establishment: (id: string) => ['establishments', id] as const,

  users: (filters?: Record<string, unknown>) =>
    filters ? (['users', filters] as const) : (['users'] as const),

  screens: (filters?: Record<string, unknown>) =>
    filters ? (['screens', filters] as const) : (['screens'] as const),
  screen: (id: string) => ['screens', id] as const,

  projects: (filters?: Record<string, unknown>) =>
    filters ? (['projects', filters] as const) : (['projects'] as const),
  project: (id: string) => ['projects', id] as const,

  templates: (filters?: Record<string, unknown>) =>
    filters ? (['templates', filters] as const) : (['templates'] as const),
  template: (id: string) => ['templates', id] as const,

  videoOverlayTemplates: ['video-overlay-templates'] as const,

  broadcasts: (filters?: Record<string, unknown>) =>
    filters ? (['broadcasts', filters] as const) : (['broadcasts'] as const),

  plans: ['billing', 'plans'] as const,
  plansAdmin: ['billing', 'plans', 'admin'] as const,
  subscription: ['billing', 'subscription'] as const,
  invoices: (filters?: Record<string, unknown>) =>
    filters ? (['billing', 'invoices', filters] as const) : (['billing', 'invoices'] as const),

  notifications: (filters?: Record<string, unknown>) =>
    filters ? (['notifications', filters] as const) : (['notifications'] as const),

  exploits: ['exploits'] as const,

  conversation: ['support', 'conversation'] as const,
  archivedConversations: (params?: Record<string, unknown>) =>
    params ? (['support', 'archive', params] as const) : (['support', 'archive'] as const),
  archivedConversation: (id: string) => ['support', 'archive', id] as const,
} as const;
